import { DOCUMENT } from '@angular/common';
import { Injectable, OnDestroy, Signal, computed, inject, signal } from '@angular/core';

import { ReportI18nService } from './report-i18n.service';

/** Thème des graphiques : leurs couleurs sont des attributs SVG, pas des variables CSS. */
export type ChartTheme = 'light' | 'dark';

/**
 * Habillage du rapport : thème et sens de lecture.
 *
 * Le document autonome portait ses propres bascules de thème et de langue, avec
 * son `localStorage` à lui. Devenu écran de l'application, le rapport n'a plus
 * à en décider : il suit `html.dark` et la langue courante, comme tous les
 * autres écrans. Deux réglages en concurrence sur la même page finiraient par
 * diverger, et l'utilisateur ne saurait plus lequel il vient d'actionner.
 *
 * Le thème se lit sur le DOM plutôt que sur `ThemeService` : ce dernier écrit la
 * classe sans l'exposer en signal, et un observateur sur `<html>` reste juste
 * même si la classe est posée ailleurs — par un autre écran, ou à l'amorçage
 * avant que ce service n'existe.
 */
@Injectable()
export class ReportChromeService implements OnDestroy {
  private readonly document = inject(DOCUMENT);
  private readonly i18n = inject(ReportI18nService);

  private readonly dark = signal(false);
  private observer?: MutationObserver;

  /** Thème courant, pour les couleurs de graphiques. */
  readonly theme: Signal<ChartTheme> = computed(() => (this.dark() ? 'dark' : 'light'));

  /** Sens de lecture, aligné sur la langue du produit. */
  readonly dir = this.i18n.dir;

  /** Vrai en arabe. Trois graphiques sur six se miroitent, les autres non. */
  readonly rtl: Signal<boolean> = computed(() => this.i18n.dir() === 'rtl');

  /** Langue courante, reprise sur l'enveloppe pour que le CSS choisisse sa police. */
  readonly lang = this.i18n.lang;

  constructor() {
    const root = this.document.documentElement;
    this.dark.set(root.classList.contains('dark'));

    // `MutationObserver` plutôt qu'un événement : la classe est posée par
    // plusieurs endroits (amorçage de l'application, en-tête, autre onglet
    // restauré) et aucun n'émet de notification.
    this.observer = new MutationObserver(() =>
      this.dark.set(root.classList.contains('dark')),
    );
    this.observer.observe(root, { attributes: true, attributeFilter: ['class'] });
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }
}
